import React, { useState } from 'react';
import { motion } from 'framer-motion';
import {
  Search, X, ShoppingCart, DollarSign, Menu,
  Zap, Settings, FileText, Users, UserPlus, BarChart3,
  Brain, Bot, Plug, Activity, MessageCircle, Hash,
  Mail, Workflow, List, User, CreditCard, TrendingUp,
  Target, Palette, LayoutGrid
} from 'lucide-react';
import styled, { useTheme } from 'styled-components';
import { theme, themeLight } from '../../styles/theme';
import { useSearch } from '../../hooks/useSearch';
import { useGoals } from '../../hooks/useGoals';
import { useVendas } from '../../hooks/useApi';
import { DefaultTheme } from 'styled-components';
import { Venda } from '../../types/api';
import { useCurrentUser } from '../../hooks/useCurrentUser';
import { useSubscriptionStatusContext } from '../../hooks/SubscriptionStatusContext';
import { useProfilePhoto } from '../../hooks/useProfilePhoto';
import { Avatar } from '../Avatar';
import { useNavigate } from 'react-router-dom';
import { SearchableItem } from '../../data/searchableItems';

// Barra de Metas Global estilizada para o Header
const GoalsBar = styled.div`
  display: flex;
  align-items: center;
  gap: ${theme.spacing[6]};
  background: none;
  margin: 0 ${theme.spacing[6]};
  margin-left: auto;
  white-space: nowrap;

  @media (max-width: 900px) {
    gap: ${theme.spacing[3]};
    margin: 0 ${theme.spacing[2]};
  }

  @media (max-width: ${theme.breakpoints.md}) {
    display: none; /* Hide goals on small screens to save space */
  }
`;
const GoalBlock = styled.div`
  display: flex;
  align-items: center;
  gap: ${theme.spacing[2]};
  padding: ${theme.spacing[2]} ${theme.spacing[3]};
  background: ${theme.colors.backgroundTertiary}40;
  border-radius: ${theme.borders.radius.lg};
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  
  &:hover {
    background: ${theme.colors.backgroundTertiary}80;
    transform: translateY(-1px);
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
  }
  
  svg {
    transition: transform 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  }
  
  &:hover svg {
    transform: scale(1.1) rotate(5deg);
  }
`;
const ProgressBar = styled.div<{ $useLight?: boolean }>`
  background: ${props => props.$useLight ? themeLight.colors.backgroundSecondary : theme.colors.backgroundSecondary};
  border: 1px solid ${props => props.$useLight ? `${themeLight.colors.border}40` : `${theme.colors.border}40`};
  border-radius: ${theme.borders.radius.full};
  width: 100px;
  height: 8px;
  overflow: hidden;
  margin-left: 8px;
  box-shadow: inset 0 2px 4px rgba(0, 0, 0, 0.1);
  
  @media (max-width: 900px) {
    width: 70px;
  }
`;
const Progress = styled.div<{ $percent: number; $color: string }>`
  height: 100%;
  width: ${props => Math.min(props.$percent, 100)}%;
  background: linear-gradient(90deg, ${props => props.$color}, ${props => props.$color}dd);
  border-radius: ${theme.borders.radius.full};
  transition: width 0.6s cubic-bezier(0.4, 0, 0.2, 1);
  box-shadow: 0 0 8px ${props => props.$color}60;
  position: relative;
  
  &::after {
    content: '';
    position: absolute;
    top: 0;
    right: 0;
    bottom: 0;
    left: 0;
    background: linear-gradient(
      90deg,
      transparent,
      rgba(255, 255, 255, 0.3),
      transparent
    );
    animation: shimmer 2s infinite;
  }
  
  @keyframes shimmer {
    0% { transform: translateX(-100%); }
    100% { transform: translateX(100%); }
  }
`;
const Value = styled.span<{ $useLight?: boolean }>`
  font-size: 13px;
  font-weight: 700;
  color: ${props => props.$useLight ? themeLight.colors.textPrimary : theme.colors.textPrimary};
  letter-spacing: -0.01em;
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  
  ${GoalBlock}:hover & {
    transform: scale(1.05);
  }
`;

const HeaderContainer = styled(motion.header)<{ $sidebarCollapsed: boolean; $useLight?: boolean }>`
  position: fixed;
  top: 0;
  left: ${props => props.$sidebarCollapsed ? '72px' : '280px'};
  right: 0;
  height: 72px;
  background: ${props => props.$useLight ? `${themeLight.colors.backgroundSecondary}f8` : `${theme.colors.backgroundSecondary}f8`};
  backdrop-filter: blur(20px) saturate(180%);
  -webkit-backdrop-filter: blur(20px) saturate(180%);
  border-bottom: 1px solid ${props => props.$useLight ? `${themeLight.colors.border}40` : `${theme.colors.border}40`};
  z-index: 1100;
  transition: left 0.4s cubic-bezier(0.4, 0, 0.2, 1), padding 0.3s ease;
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 0 ${theme.spacing[6]};
  box-shadow: 0 4px 24px rgba(0, 0, 0, 0.06);
  will-change: left;
  width: calc(100% - ${props => props.$sidebarCollapsed ? '72px' : '280px'});

  @media (max-width: ${theme.breakpoints.lg}) {
    left: 0 !important;
    width: 100% !important;
    padding: 0 ${theme.spacing[4]};
    height: 64px;
  }

  @media (max-width: ${theme.breakpoints.sm}) {
    padding: 0 ${theme.spacing[3]};
    height: 60px;
  }
`;

// HeaderBackdrop removed to avoid covering header content

const MobileMenuButton = styled.button<{ $useLight?: boolean }>`
  display: none;
  align-items: center;
  justify-content: center;
  width: 40px;
  height: 40px;
  background: ${props => props.$useLight ? themeLight.colors.backgroundTertiary : theme.colors.backgroundTertiary};
  border: 1px solid ${props => props.$useLight ? themeLight.colors.border : theme.colors.border};
  border-radius: ${theme.borders.radius.lg};
  color: ${props => props.$useLight ? themeLight.colors.textSecondary : theme.colors.textSecondary};
  cursor: pointer;
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);

  &:hover {
    background: ${props => props.$useLight ? themeLight.colors.primary : theme.colors.primary};
    color: white;
    border-color: ${props => props.$useLight ? themeLight.colors.primary : theme.colors.primary};
    transform: scale(1.05);
    box-shadow: 0 4px 12px ${props => props.$useLight ? `${themeLight.colors.primary}40` : `${theme.colors.primary}40`};
  }
  
  &:active {
    transform: scale(0.95);
  }

  @media (max-width: ${theme.breakpoints.lg}) {
    display: flex;
  }
`;

const HeaderLeft = styled.div`
  display: flex;
  align-items: center;
  gap: ${theme.spacing[4]};
`;


const SearchContainer = styled.div`
  position: relative;
  display: flex;
  align-items: center;
`;

const SearchInput = styled.input<{ $useLight?: boolean }>`
  width: 320px;
  padding: ${theme.spacing[3]} ${theme.spacing[4]} ${theme.spacing[3]} ${theme.spacing[12]};
  background: ${props => props.$useLight ? themeLight.colors.backgroundTertiary : theme.colors.backgroundTertiary};
  border: 1px solid ${props => props.$useLight ? `${themeLight.colors.border}60` : `${theme.colors.border}60`};
  border-radius: ${theme.borders.radius.full};
  color: ${props => props.$useLight ? themeLight.colors.textPrimary : theme.colors.textPrimary};
  font-size: ${theme.typography.fontSize.sm};
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.05);

  &:focus {
    outline: none;
    border-color: ${props => props.$useLight ? themeLight.colors.primary : theme.colors.primary};
    box-shadow: 0 0 0 3px ${props => props.$useLight ? `${themeLight.colors.primary}20` : `${theme.colors.primary}20`},
                0 4px 12px rgba(0, 0, 0, 0.1);
    width: 420px;
    background: ${props => props.$useLight ? themeLight.colors.background : theme.colors.background};
  }

  &::placeholder {
    color: ${props => props.$useLight ? themeLight.colors.textTertiary : theme.colors.textTertiary};
  }

  @media (max-width: ${theme.breakpoints.xl}) {
    width: 280px;
    &:focus {
      width: 360px;
    }
  }

  @media (max-width: ${theme.breakpoints.lg}) {
    width: 220px;
    &:focus {
      width: 300px;
    }
  }

  @media (max-width: ${theme.breakpoints.md}) {
    width: 180px;
    &:focus {
      width: 240px;
    }
  }

  @media (max-width: ${theme.breakpoints.sm}) {
    width: 150px;
    &:focus {
      width: 200px;
    }
  }
`;

const SearchIcon = styled(Search)<{ $useLight?: boolean }>`
  position: absolute;
  left: ${theme.spacing[4]};
  color: ${props => props.$useLight ? themeLight.colors.textTertiary : theme.colors.textTertiary};
  width: 16px;
  height: 16px;
`;

const SearchResults = styled(motion.div)<{ $useLight?: boolean }>`
  position: absolute;
  top: 100%;
  left: 0;
  right: 0;
  background: ${props => props.$useLight ? themeLight.colors.backgroundSecondary : theme.colors.backgroundSecondary};
  border: 1px solid ${props => props.$useLight ? themeLight.colors.border : theme.colors.border};
  border-radius: ${theme.borders.radius.lg};
  box-shadow: ${theme.shadows.lg};
  z-index: 1000;
  max-height: 400px;
  overflow-y: auto;
  margin-top: ${theme.spacing[2]};
`;

const SearchResultSection = styled.div<{ $useLight?: boolean }>`
  padding: ${theme.spacing[3]};
  border-bottom: 1px solid ${props => props.$useLight ? themeLight.colors.border : theme.colors.border};
  
  &:last-child {
    border-bottom: none;
  }
`;

const SearchResultTitle = styled.h4<{ $useLight?: boolean }>`
  font-size: ${theme.typography.fontSize.sm};
  font-weight: ${theme.typography.fontWeight.semibold};
  color: ${props => props.$useLight ? themeLight.colors.textPrimary : theme.colors.textPrimary};
  margin: 0 0 ${theme.spacing[2]} 0;
`;

const SearchResultItem = styled.div<{ $useLight?: boolean }>`
  padding: ${theme.spacing[2]};
  border-radius: ${theme.borders.radius.md};
  cursor: pointer;
  transition: background-color ${theme.animations.duration.fast} ${theme.animations.easing.easeInOut};
  
  &:hover {
    background: ${props => props.$useLight ? themeLight.colors.backgroundTertiary : theme.colors.backgroundTertiary};
  }
`;

const SearchResultName = styled.div<{ $useLight?: boolean }>`
  display: flex;
  align-items: center;
  font-size: ${theme.typography.fontSize.sm};
  font-weight: ${theme.typography.fontWeight.medium};
  color: ${props => props.$useLight ? themeLight.colors.textPrimary : theme.colors.textPrimary};
  margin-bottom: ${theme.spacing[1]};
`;

const SearchResultDescription = styled.div<{ $useLight?: boolean }>`
  font-size: ${theme.typography.fontSize.xs};
  color: ${props => props.$useLight ? themeLight.colors.textSecondary : theme.colors.textSecondary};
`;

const NoResults = styled.div<{ $useLight?: boolean }>`
  padding: ${theme.spacing[4]};
  text-align: center;
  color: ${props => props.$useLight ? themeLight.colors.textTertiary : theme.colors.textTertiary};
  font-size: ${theme.typography.fontSize.sm};
`;

const HeaderRight = styled.div`
  display: flex;
  align-items: center;
  gap: ${theme.spacing[4]};
`;
const UserMenu = styled.div<{ $useLight?: boolean }>`
  display: flex;
  align-items: center;
  gap: ${theme.spacing[3]};
  padding: ${theme.spacing[2]} ${theme.spacing[4]};
  background: ${props => props.$useLight ? themeLight.colors.backgroundTertiary : theme.colors.backgroundTertiary};
  border: 1px solid ${props => props.$useLight ? `${themeLight.colors.border}40` : `${theme.colors.border}40`};
  border-radius: ${theme.borders.radius.full};
  cursor: pointer;
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.08);
  
  &:hover {
    background: ${props => props.$useLight 
      ? themeLight.colors.backgroundSecondary 
      : theme.colors.backgroundSecondary};
    border-color: ${props => props.$useLight ? themeLight.colors.primary : theme.colors.primary}60;
    box-shadow: 0 4px 16px rgba(0, 0, 0, 0.12);
    transform: translateY(-1px);
  }
  
  &:active {
    transform: scale(0.98);
  }
`;


const UserInfo = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  max-width: 120px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

const UserName = styled.span<{ $useLight?: boolean }>`
  font-size: ${theme.typography.fontSize.sm};
  font-weight: ${theme.typography.fontWeight.medium};
  color: ${props => props.$useLight ? themeLight.colors.textPrimary : theme.colors.textPrimary};
`;

const UserRole = styled.span<{ $useLight?: boolean }>`
  font-size: ${theme.typography.fontSize.xs};
  color: ${props => props.$useLight ? themeLight.colors.textTertiary : theme.colors.textTertiary};
`;

interface HeaderProps {
  sidebarCollapsed: boolean;
  onToggleSidebar?: () => void;
}

// Mapeamento de ícones
const iconMap: Record<string, any> = {
  LayoutGrid, Users, UserPlus, ShoppingCart, DollarSign, BarChart3,
  Brain, Bot, Plug, FileText, Activity, MessageCircle, Hash,
  Mail, Workflow, List, User, CreditCard, TrendingUp,
  Target, Settings, Palette, Zap
};

// Ícones padrão por categoria
const defaultCategoryIcons = {
  acao: Zap,
  configuracao: Settings,
  pagina: FileText
};

// Componente para renderizar o ícone
const ItemIcon: React.FC<{ iconName?: string; category: string; useLight?: boolean }> = ({ iconName, category, useLight }) => {
  const IconComponent = iconName ? iconMap[iconName] : defaultCategoryIcons[category as keyof typeof defaultCategoryIcons];
  const iconColor = category === 'acao'
    ? theme.colors.warning
    : category === 'configuracao'
    ? theme.colors.info
    : theme.colors.primary;

  return IconComponent ? <IconComponent size={16} color={iconColor} style={{ marginRight: '8px', flexShrink: 0 }} /> : null;
};

const Header: React.FC<HeaderProps> = ({ sidebarCollapsed, onToggleSidebar }) => {
  const { searchTerm, setSearchTerm, searchResults, hasResults } = useSearch();
  const [showResults, setShowResults] = useState(false);
  const { metaVendas, metaReceita } = useGoals();
  const { user } = useCurrentUser();
  const { status: subscriptionStatus } = useSubscriptionStatusContext();
  const { profilePhoto } = useProfilePhoto();
  const navigate = useNavigate();
  const [profileOverrides, setProfileOverrides] = useState<{
    displayName?: string;
    photoURL?: string;
  }>({});

  // Usar vendas pagas reais para metas
  const { data: vendasData } = useVendas();
  const vendas: Venda[] = vendasData?.data?.vendas || [];
  const vendasPagas = vendas.filter((v: Venda) => v.status?.toLowerCase() === 'pago');
  const vendasRealizadas = vendasPagas.length;
  const receitaAtual = vendasPagas.reduce((sum: number, v: Venda) => {
    // valor pode vir como string 'R$ 123,45' ou número
    let valor = v.valor;
    if (typeof valor === 'string') {
      valor = valor.replace('R$', '').replace('.', '').replace(',', '.');
    }
    return sum + (parseFloat(valor) || 0);
  }, 0);
  const progressoVendas = metaVendas > 0 ? Math.min(100, Math.round((vendasRealizadas / metaVendas) * 100)) : 0;
  const progressoReceita = metaReceita > 0 ? Math.min(100, Math.round((receitaAtual / metaReceita) * 100)) : 0;

  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSearchTerm(e.target.value);
    setShowResults(e.target.value.length > 0);
  };

  const handleClearSearch = () => {
    setSearchTerm('');
    setShowResults(false);
  };

  // Função para obter o nome do plano de assinatura
  const getSubscriptionPlanName = () => {
    if (subscriptionStatus.loading) {
      return 'Verificando...';
    }

    if (subscriptionStatus.error || !subscriptionStatus.plano) {
      return 'Conta Google';
    }

    // Formatar o nome do plano
    const plano = subscriptionStatus.plano.trim();
    if (plano === '' || plano === 'null') {
      return 'Conta Google';
    }

    // Capitalizar primeira letra e mostrar o plano
    return plano.charAt(0).toUpperCase() + plano.slice(1);
  };

  // Escutar mudanças do perfil
  React.useEffect(() => {
    // Carregar overrides salvos no localStorage na inicialização
    try {
      const savedOverrides = localStorage.getItem('loopra_profile_updates');
      if (savedOverrides) {
        const overrides = JSON.parse(savedOverrides);
        // Só usar se for recente (último minuto)
        if (Date.now() - overrides.timestamp < 60000) {
          setProfileOverrides(overrides);
        }
      }
    } catch (error) {
      console.error('Erro ao carregar profile overrides:', error);
    }

    // Escutar evento customizado de atualização de perfil
    const handleProfileUpdate = (event: CustomEvent) => {
      const updateData = event.detail;
      setProfileOverrides(prev => ({
        ...prev,
        ...updateData
      }));
    };

    window.addEventListener('loopra-profile-updated', handleProfileUpdate as EventListener);

    return () => {
      window.removeEventListener('loopra-profile-updated', handleProfileUpdate as EventListener);
    };
  }, []);

  // Detectar se o tema atual é o tema claro (themeLight)
  const currentTheme = useTheme() as DefaultTheme;
  const useLight = currentTheme?.colors?.background === themeLight.colors.background;

  return (
  <HeaderContainer
      $sidebarCollapsed={sidebarCollapsed}
      $useLight={useLight}
      initial={{ y: -80 }}
      animate={{ y: 0 }}
      transition={{ duration: 0.3, ease: 'easeOut' }}
    >
      <HeaderRight>
        <UserMenu $useLight={useLight} onClick={() => window.location.href = '/profile'} title="Ir para perfil">
          {user ? (
            <>
              <Avatar
                name={profileOverrides.displayName || user.displayName || undefined}
                email={user.email || undefined}
                size="sm"
                src={profilePhoto || profileOverrides.photoURL || user.photoURL || undefined}
                alt={profileOverrides.displayName || user.displayName || 'Usuário'}
              />
              <UserInfo>
                <UserName
                  $useLight={useLight}
                  title={profileOverrides.displayName || user.displayName || user.email || 'Usuário'}
                >
                  {profileOverrides.displayName || user.displayName || user.email || 'Usuário'}
                </UserName>
                <UserRole $useLight={useLight} title={getSubscriptionPlanName()}>{getSubscriptionPlanName()}</UserRole>
              </UserInfo>
            </>
          ) : (
            <UserInfo><UserName $useLight={useLight}>Desconectado</UserName></UserInfo>
          )}
        </UserMenu>
        {/* Barra de Metas Global */}
        {(metaVendas > 0 || metaReceita > 0) && (
          <GoalsBar>
            <GoalBlock>
              <ShoppingCart size={16} color={theme.colors.success} />
              <Value $useLight={useLight}>{vendasRealizadas} / {metaVendas > 0 ? metaVendas : '--'}</Value>
              <ProgressBar $useLight={useLight}>
                <Progress $percent={progressoVendas} $color={theme.colors.success} />
              </ProgressBar>
            </GoalBlock>
            <GoalBlock>
              <DollarSign size={16} color={theme.colors.primary} />
              <Value $useLight={useLight}>R$ {receitaAtual.toLocaleString('pt-BR', { minimumFractionDigits: 2 })} / R$ {metaReceita > 0 ? metaReceita.toLocaleString('pt-BR', { minimumFractionDigits: 2 }) : '--'}</Value>
              <ProgressBar $useLight={useLight}>
                <Progress $percent={progressoReceita} $color={theme.colors.primary} />
              </ProgressBar>
            </GoalBlock>
          </GoalsBar>
        )}
      </HeaderRight>
      <HeaderLeft>
        <MobileMenuButton $useLight={useLight} onClick={onToggleSidebar}>
          <Menu size={20} />
        </MobileMenuButton>
        <SearchContainer>
          <SearchIcon $useLight={useLight} />
          <SearchInput
            placeholder="Buscar páginas, configurações, ações..."
            value={searchTerm}
            onChange={handleSearchChange}
            onFocus={() => setShowResults(searchTerm.length > 0)}
            onBlur={() => setTimeout(() => setShowResults(false), 200)}
            $useLight={useLight}
          />
          {searchTerm && (
            <button
              onClick={handleClearSearch}
              style={{
                position: 'absolute',
                right: '12px',
                background: 'none',
                border: 'none',
                color: theme.colors.textTertiary,
                cursor: 'pointer',
                padding: '4px'
              }}
            >
              <X size={16} />
            </button>
          )}
          {showResults && (
            <SearchResults
              $useLight={useLight}
              initial={{ opacity: 0, y: -10 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -10 }}
            >
              {hasResults ? (
                <SearchResultSection $useLight={useLight}>
                  <SearchResultTitle $useLight={useLight}>Páginas e Ações ({searchResults.navegacao.length})</SearchResultTitle>
                  {searchResults.navegacao.slice(0, 8).map((item: SearchableItem) => (
                    <SearchResultItem
                      $useLight={useLight}
                      key={item.id}
                      onClick={() => {
                        navigate(item.path);
                        setShowResults(false);
                        setSearchTerm('');
                      }}
                    >
                      <SearchResultName $useLight={useLight}>
                        <ItemIcon iconName={item.icon} category={item.category} useLight={useLight} />
                        {item.title}
                      </SearchResultName>
                      <SearchResultDescription $useLight={useLight}>
                        {item.description}
                      </SearchResultDescription>
                    </SearchResultItem>
                  ))}
                </SearchResultSection>
              ) : (
                <NoResults $useLight={useLight}>Nenhum resultado encontrado</NoResults>
              )}
            </SearchResults>
          )}
        </SearchContainer>
      </HeaderLeft>
    </HeaderContainer>
  );
};

export default Header;

